import {groupByUniqueKey} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import navigateToURL from 'lib/navigateToURL';
import MonomerConfigurationPageContainer, {
  MONOMER_CONFIGURATION_PURPOSE,
} from 'molbio/monomers/admin/MonomerConfigurationPageContainer';
import AliasCreationForm from 'molbio/monomers/custom-syntax/AliasCreationForm';
import AliasTable, {updateAliases} from 'molbio/monomers/custom-syntax/AliasTable';
import SyntaxCreationModal from 'molbio/monomers/custom-syntax/SyntaxCreationModal';
import {
  AliasData,
  createAliases,
  getCustomSyntaxes,
  VendorModificationSyntax,
} from 'molbio/monomers/MonomersDAO';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

function InlinePre({children}: {children: string}): JSX.Element | null {
  if (children.length === 0) {
    return null;
  }
  return <pre style={{display: 'inline', padding: 0}}>{children}</pre>;
}

function ConfigurationDisplay({syntax}: {syntax: VendorModificationSyntax}): JSX.Element {
  return (
    <div style={{width: '300px'}}>
      <p>Case sensitive? {syntax.isCaseSensitive ? <b>YES</b> : <b>NO</b>} </p>
      <p>
        Extract terminals to schema fields?{' '}
        {syntax.configuration.PREFILTER_TERMINALS ? <b>YES</b> : <b>NO</b>}{' '}
      </p>
      {syntax.configuration.PREFILTER_TERMINALS && (
        <p>
          5' left delimiter:{' '}
          <InlinePre>{syntax.configuration.PREFILTER_TERMINALS.five_prime_left_delimiter ?? ''}</InlinePre>
          <br />
          5' right delimiter:{' '}
          <InlinePre>{syntax.configuration.PREFILTER_TERMINALS.five_prime_right_delimiter}</InlinePre>
          <br />
          3' left delimiter:{' '}
          <InlinePre>{syntax.configuration.PREFILTER_TERMINALS.three_prime_left_delimiter}</InlinePre>
          <br />
          3' right delimiter:{' '}
          <InlinePre>{syntax.configuration.PREFILTER_TERMINALS.three_prime_right_delimiter ?? ''}</InlinePre>
          <br />
          Allow up to 2 terminals at each end?{' '}
          {(syntax.configuration.PREFILTER_TERMINALS.max_depth ?? 1) > 1 ? <b>YES</b> : <b>NO</b>}
        </p>
      )}
      <p>
        Use shared delimiter?{' '}
        {syntax.configuration.USE_SHARED_DELIMITER ? (
          <span>
            <b>YES</b>: <InlinePre>{syntax.configuration.USE_SHARED_DELIMITER.delimiter}</InlinePre>
          </span>
        ) : (
          <b>NO</b>
        )}
      </p>
      <p>
        Always set last phosphate to None?{' '}
        {syntax.configuration.NULLIFY_ENDING_PHOSPHATE ? <b>YES</b> : <b>NO</b>}
      </p>
    </div>
  );
}

export default function CustomSyntaxSection({
  hideNavHeader,
  initialVendorSyntaxId,
}: {
  hideNavHeader?: boolean;
  initialVendorSyntaxId?: string;
}): JSX.Element {
  const [syntaxesById, setSyntaxesById] = useState<{[id: number]: VendorModificationSyntax}>({});
  const [activeSyntaxId, setActiveSyntaxId] = useState<number | undefined>(undefined);

  useEffect(() => {
    async function fetchSyntaxes(): Promise<void> {
      const syntaxes = await getCustomSyntaxes();
      setSyntaxesById(groupByUniqueKey(syntaxes, (s) => s.id));

      if (initialVendorSyntaxId) {
        const targetSyntax = syntaxes.find((s) => s.apiId === initialVendorSyntaxId);
        if (targetSyntax) {
          setActiveSyntaxId(targetSyntax.id);
        } else if (syntaxes.length) {
          setActiveSyntaxId(syntaxes[0].id);
        }
      } else if (syntaxes.length) {
        setActiveSyntaxId(syntaxes[0].id);
      }
    }
    fetchSyntaxes();
  }, [initialVendorSyntaxId]);

  const activeSyntax = activeSyntaxId && syntaxesById[activeSyntaxId];

  useEffect(() => {
    if (activeSyntax) {
      const newUrl = `/monomers/custom-notations/${activeSyntax.apiId}`;
      navigateToURL(newUrl, {skipTrigger: false});
    }
  }, [activeSyntax]);

  return (
    <>
      {hideNavHeader ? (
        <h1>Custom syntax</h1>
      ) : (
        <MonomerConfigurationPageContainer purpose={MONOMER_CONFIGURATION_PURPOSE.CUSTOM_NOTATION} />
      )}
      <div className={css(styles.customSyntaxSection)}>
        <div className={css(styles.customSyntaxHeader)}>
          <div className={css(styles.syntaxSelector)}>
            <SelectDropdown
              buttonSize={ButtonSize.MEDIUM}
              displayName={activeSyntax ? activeSyntax.name : 'Select ...'}
              menu={
                <Menu width={MenuWidth.FIT_CONTENT}>
                  {Object.values(syntaxesById).map((s) => (
                    <Option
                      key={s.id}
                      value={s.id}
                      onSelect={(val: number) => {
                        setActiveSyntaxId(val);
                      }}
                    >
                      {s.name}
                    </Option>
                  ))}
                </Menu>
              }
            />
            <Button
              className={css(styles.createButton)}
              icon="plus"
              size={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyle.PRIMARY}
              onClick={async () => {
                const newSyntax = await legacyRunModal(SyntaxCreationModal, {});
                if (newSyntax) {
                  setSyntaxesById((oldValue) => ({...oldValue, [newSyntax.id]: newSyntax}));
                  setActiveSyntaxId(newSyntax.id);
                }
              }}
              text="Create new"
            />
          </div>
          {activeSyntax && (
            <Tooltip text={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
              <Button
                icon="audit-trail"
                onClick={() => {
                  exportAuditLogs([activeSyntax.apiId]);
                }}
                size={ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
              />
            </Tooltip>
          )}
        </div>
        <div className={css(styles.customSyntaxInput)}>
          {activeSyntax && (
            <>
              <ConfigurationDisplay syntax={activeSyntax} />
              <AliasCreationForm
                onSubmit={(formText: string) => createAliases(activeSyntaxId, formText)}
                updateLocalState={(newAliases: Array<AliasData>) => {
                  setSyntaxesById((oldValue) => ({
                    ...oldValue,
                    [activeSyntaxId]: {...activeSyntax, aliases: [...activeSyntax.aliases, ...newAliases]},
                  }));
                }}
                syntaxName={activeSyntax.name}
              />
              <AliasTable
                aliases={activeSyntax.aliases}
                syntaxName={activeSyntax.name}
                updateLocalState={(updatedAlias: AliasData, isDelete: boolean = false) => {
                  setSyntaxesById((oldValue) => ({
                    ...oldValue,
                    [activeSyntaxId]: {
                      ...activeSyntax,
                      aliases: updateAliases(updatedAlias, activeSyntax.aliases, isDelete),
                    },
                  }));
                }}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  customSyntaxSection: {
    marginLeft: 40,
    marginRight: 40,
    flex: 1,
    flexDirection: 'column',
    display: 'flex',
  },
  customSyntaxHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20,
    marginTop: 20,
    justifyContent: 'space-between',
  },
  syntaxSelector: {
    display: 'flex',
    alignItems: 'center',
  },
  customSyntaxInput: {
    display: 'flex',
    marginBottom: 20,
    minHeight: 400,
  },
  createButton: {
    marginLeft: 20,
  },
});
